import {
    clampInterval,
    intervalArrayFromState,
    intervalFromState,
    intervalsOverlap
} from "../../Helpers/IntervalHelper";
import { addDays, differenceInMinutes, endOfDay, isBefore, isSameDay, startOfDay } from "date-fns";
import { dateFromState } from "../../Helpers/DateTimeHelper";

/**
 * This module contains methods for checking / validating availability
 */

/**
 * Check the given interval for every possible error
 * @param interval
 * @param config
 * @param availability
 * @returns {object}
 */
export function validateInterval (interval, config, availability) {
    const { is_variable } = config;

    const errors = [];
    interval = intervalFromState(interval);

    if (!interval || !interval.start) {
        errors.push('Invalid interval');
        return {isValid: false, errors: errors};
    }

    if (interval.start < new Date()) {
        errors.push('Please select a date/time in the future');
    }

    if (intervalClashes(interval, availability.blockedIntervals)) {
        errors.push('Timeslot unavailable');
    }

    if (availability.timeslotsRemaining !== null && availability.timeslotsRemaining < 1) {
        errors.push('Max slots booked for this amenity on this day');
    }

    if (!isBefore(interval.start, interval.end)) {
        errors.push('Booking must have a duration');
    }

    if (availability.boundary) {
        const boundaryInterval = intervalFromState(availability.boundary);
        if ((interval.end > boundaryInterval.end) || (interval.start < availability.boundary.start)) {
            // NOTE - user should not see this. Perhaps they selected an interval before the availability finished loading?
            errors.push('Please select a date/time that starts within the current booking date');
        }
    }

    if (is_variable) {
        if (differenceInMinutes(interval.end, interval.start) > config.duration) {
            errors.push(`Booking cannot exceed ${(config.duration)} minutes`);
        }
    } else {
        if (differenceInMinutes(interval.end, interval.start) > (config.duration * config.max_per_day)) {
            errors.push(`Booking cannot exceed ${config.max_per_day} timeslots`);
        }
    }

    if (errors.length > 0) {
        return {isValid: false, errors: errors};
    } else {
        return {isValid: true, errors: errors};
    }
}

/**
 * @param availability          Availability object as received in the server response
 * @param config                Booking config object as received in the server response
 * @returns {JSX.Element|string|null}
 */
export function validateDay ({
    timeslotsRemaining,
    timeslotsBookedCounts
}, config) {
    if (timeslotsRemaining > 0) {
        return null;
    }

    if (!timeslotsBookedCounts || !timeslotsBookedCounts['active']) {
        return null;
    }

    const typeSentence = (config.is_variable === 1) ? 'bookings' : 'booked timeslots';

    /** Check for total active bookings limit **/
    if (config.max_active_bookings && (timeslotsBookedCounts['active'] >= config.max_active_bookings)) {
        return <span>
            You’ve reached the maximum number of upcoming {typeSentence} at this amenity.
            The limit is {config.max_active_bookings}.
            You will be able to book again after your next booking finishes, or you can cancel an existing booking.
        </span>;
    }

    /**
     * Check for periodic booking limits.
     * Note that if multiple periodic limits are met, the larger will be
     * detailed in the message, as that will be more significant to the user
     */
    let periodString = null;
    let quantityString = null;

    if (config.max_per_day && (timeslotsBookedCounts['today'] >= config.max_per_day)) {
        periodString = `daily`;
        quantityString = `${config.max_per_day} per day`;
    }

    if (config.max_per_week && (timeslotsBookedCounts['this_week'] >= config.max_per_week)) {
        periodString = `weekly`;
        quantityString = `${config.max_per_week} per week`;
    }

    if (config.max_per_month && (timeslotsBookedCounts['this_month'] >= config.max_per_month)) {
        periodString = `monthly`;
        quantityString = `${config.max_per_month} per month`;
    }

    if (quantityString) {
        return <span>
            You’ve reached the maximum {periodString} {typeSentence} at this amenity.
            The limit is {quantityString}.
            Please try the Next Available button below or cancel an existing booking.
        </span>;
    }

    return "Maximum slots booked for this amenity on this day.";
}


/**
 * Determines if there are any opening hours for the current date time.
 * Ignores current time ie doesn't check if it's open NOW.
 */
export function isOpenOnThisDay (date, openingHours) {
    if (!date || !openingHours || !openingHours.length) {
        return false;
    }

    date = dateFromState(date);
    openingHours = intervalArrayFromState(openingHours);

    for (var i in openingHours) {
        if (isSameDay(openingHours[i].start, date)
            || isSameDay(openingHours[i].end, date)
        ) {
            return true;
        }
    }
    return false;
}

export function intervalClashes (interval, compareIntervals) {
    interval = intervalFromState(interval);
    compareIntervals = intervalArrayFromState(compareIntervals);

    for (let i in compareIntervals) {
        if (intervalsOverlap(interval, compareIntervals[i])) {
            return true;
        }
    }
    return false;
}


/**
 * @deprecated -- DONE ON SERVER NOW
 * Checks if we can extend the current booking interval based on the timeslots
 * remaining on the booking day and the next day, and the number of timeslots left
 * @param earlierOrLater
 * @param interval      The interval that we want to maybe extend
 * @param slotsRemainingThisDay    The number of timeslots renaming on the day of the selectedInterval start
 * @param slotsRemainingNextDay    The number of timeslots renaming on the day after the day of the selectedInterval start
 * @param configDuration        The length of each fixed slot as defined in the booking config;
 */
export const sufficientTimeslotsToExtendFixedBooking = (
    earlierOrLater,
    interval,
    slotsRemainingThisDay,
    slotsRemainingNextDay,
    configDuration
) => {
    if (!interval.start || !interval.end) {
        return false;
    }

    // Compare the current the length in minutes of the currently selected slot,
    // as if we're editing a previously created booking we might have a
    // 'big slot' that's effectively multiple slots merged into one
    let current = intervalFromState(interval);

    // Perhaps selectedInterval was not in correct format
    if (isNaN(current.start.getTime()) || isNaN(current.end.getTime())) {
        return false;
    }

    let dayPeriod = {
        start: startOfDay(current.start),
        end: startOfDay(addDays(current.start, 1))
    };

    /** Can extend booking earlier? **/
    if (earlierOrLater === 'earlier') {
        return canExtendBookingForThisPeriod(current, dayPeriod, slotsRemainingThisDay, configDuration);
    }

    /* Can extend booking later */
    const crossingMidnightBoundary = (!isSameDay(current.start, current.end));
    if (crossingMidnightBoundary) {
        dayPeriod = {
            start: startOfDay(current.end),
            end: current.end
        }
    }
    return canExtendBookingForThisPeriod(current, dayPeriod, slotsRemainingNextDay, configDuration);
}

/**
 * @deprecated DONE ON SERVER NOW
 * @param currentInterval
 * @param period
 * @param timeslotsRemainingForPeriod
 * @param configDuration
 * @returns {boolean}
 */
const canExtendBookingForThisPeriod = (currentInterval, period, timeslotsRemainingForPeriod, configDuration) => {
    let clamped = clampInterval(currentInterval, period)
    let currentBookingMinutes = differenceInMinutes(clamped.end, clamped.start)
    let minutesLimit = timeslotsRemainingForPeriod * configDuration;

    return !(
        timeslotsRemainingForPeriod !== null
        && (currentBookingMinutes + configDuration) > minutesLimit
    );
}
